import React, { useMemo, useState } from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * LegacyBatchPicker — Sub-sprint D-β-β.
 *
 * Textarea for JSON array of `_BatchItem`. Client-side schema parse +
 * cross-org validator. Disables dry-run until parse succeeds. Cap 200
 * rows in v0. Items with bucket=BACKFILL and organisation_id ≠
 * pilotOrgId are rejected with a per-row error.
 */
const VALID_BUCKETS = new Set(["BACKFILL", "SOFT_REVOKE", "EXCLUDE"]);

const _fmt = (tpl, vars) => {
  if (!tpl) return "";
  return tpl.replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
};

export const LegacyBatchPicker = ({ value, onChange, disabled = false, pilotOrgId = "TEAMSOFTWAREINNOVATIONS", maxRows = 200 }) => {
  const t = useRacketLabAdminT();
  const [text, setText] = useState(value?.raw || "");
  const parsed = useMemo(() => {
    if (!text.trim()) return { items: [], error: null, rowErrors: [] };
    try {
      const obj = JSON.parse(text);
      if (!Array.isArray(obj)) return { items: [], error: t("ops.legacy.batch.parse_error_array"), rowErrors: [] };
      if (obj.length > maxRows) return { items: [], error: _fmt(t("ops.legacy.batch.parse_error_too_many"), { n: obj.length, max: maxRows }), rowErrors: [] };
      const rowErrors = obj.map((it, i) => {
        if (typeof it !== "object" || it === null) return _fmt(t("ops.legacy.batch.row_error_not_object"), { i });
        if (!it.user_id || typeof it.user_id !== "string") return _fmt(t("ops.legacy.batch.row_error_user_id"), { i });
        if (!VALID_BUCKETS.has(it.bucket)) return _fmt(t("ops.legacy.batch.row_error_bucket"), { i });
        if (it.bucket === "BACKFILL" && it.organisation_id && it.organisation_id !== pilotOrgId) {
          return _fmt(t("ops.legacy.batch.row_error_cross_org"), { i, org: it.organisation_id });
        }
        return null;
      }).filter(Boolean);
      return { items: obj, error: rowErrors.length ? _fmt(t("ops.legacy.batch.row_error_count"), { count: rowErrors.length }) : null, rowErrors };
    } catch (e) {
      return { items: [], error: _fmt(t("ops.legacy.batch.parse_error_json"), { msg: e.message }), rowErrors: [] };
    }
  }, [text, pilotOrgId, maxRows, t]);

  React.useEffect(() => {
    onChange?.({ raw: text, items: parsed.items, valid: !parsed.error && parsed.items.length > 0 });
  }, [text, parsed.error, parsed.items.length, parsed.items, onChange]);

  const counts = useMemo(() => {
    const c = { BACKFILL: 0, SOFT_REVOKE: 0, EXCLUDE: 0 };
    parsed.items.forEach((it) => { if (c[it.bucket] !== undefined) c[it.bucket] += 1; });
    return c;
  }, [parsed.items]);

  return (
    <div data-testid="rl-ops-legacy-batch-picker" className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">
          {_fmt(t("ops.legacy.batch.json_label"), { shape: "{user_id, bucket, …}", maxRows })}
        </span>
        <textarea
          data-testid="rl-ops-legacy-batch-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
          rows={6}
          placeholder={`[{"user_id":"u1","bucket":"SOFT_REVOKE","reason":"…"}]`}
          className="rounded-md border border-slate-300 px-2 py-1 font-mono text-xs"
        />
      </label>

      {parsed.error && (
        <div data-testid="rl-ops-legacy-batch-parse-error" className="rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800">
          {parsed.error}
          {parsed.rowErrors.length > 0 && (
            <ul className="ml-4 list-disc">
              {parsed.rowErrors.slice(0, 10).map((err, i) => (<li key={i}>{err}</li>))}
              {parsed.rowErrors.length > 10 && (<li>{_fmt(t("ops.legacy.batch.row_more"), { count: parsed.rowErrors.length - 10 })}</li>)}
            </ul>
          )}
        </div>
      )}

      {parsed.items.length > 0 && !parsed.error && (
        <>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span data-testid="rl-ops-legacy-batch-items-count" className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-800">
              items_total: <code>{parsed.items.length}</code>
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5">BACKFILL: <code>{counts.BACKFILL}</code></span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5">SOFT_REVOKE: <code>{counts.SOFT_REVOKE}</code></span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5">EXCLUDE: <code>{counts.EXCLUDE}</code></span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200" data-testid="rl-ops-legacy-batch-preview-table">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr><th className="px-2 py-1">#</th><th className="px-2 py-1">user_id</th><th className="px-2 py-1">bucket</th><th className="px-2 py-1">role</th><th className="px-2 py-1">organisation_id</th><th className="px-2 py-1">reason</th></tr>
              </thead>
              <tbody>
                {parsed.items.slice(0, 50).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-slate-500">{i}</td>
                    <td className="px-2 py-1 font-mono">{(it.user_id || "").slice(0, 18)}</td>
                    <td className="px-2 py-1 font-mono">{it.bucket}</td>
                    <td className="px-2 py-1 font-mono">{it.role || "—"}</td>
                    <td className="px-2 py-1 font-mono">{it.organisation_id || "—"}</td>
                    <td className="px-2 py-1 truncate">{(it.reason || "").slice(0, 30)}</td>
                  </tr>
                ))}
                {parsed.items.length > 50 && (
                  <tr><td colSpan={6} className="px-2 py-1 text-center text-slate-400">{_fmt(t("ops.legacy.batch.preview_more"), { count: parsed.items.length - 50 })}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LegacyBatchPicker;
